import { uploadFile } from '@/shared/api'
import { dateTimeInputToIso, type FileRef, formatDateTime, zoneTag } from '@/shared/lib'
import { Checkbox } from '@/shared/ui/checkbox'
import { Field, Input, Textarea } from '@/shared/ui/field'
import { FileDrop } from '@/shared/ui/file-drop'
import { FileRow } from '@/shared/ui/file-row'
import { zoneFieldHint } from '@/shared/ui/zone-badge'

// Черновик встречи: форма заведения и форма правки — одни и те же поля, поэтому
// блок живёт у сущности, а фичи приносят только свою мутацию (импорт фича→фича
// запрещён FSD).
export interface MeetingDraft {
  title: string
  /** Значение <input type="datetime-local">. */
  when: string
  location: string
  participants: string
  url: string
  notes: string
  customer_visible: boolean
  attachments: FileRef[]
}

export const emptyMeetingDraft: MeetingDraft = {
  title: '',
  when: '',
  location: '',
  participants: '',
  url: '',
  notes: '',
  customer_visible: false,
  attachments: [],
}

// Встреча уезжает фактом в календарь и становится там событием — под полем
// показываем, каким именно: момент в зоне устройства, то есть ровно то время,
// которое человек и набирает. Пока оно не задано — хотя бы зона, которой поле
// считает ввод.
function whenHint(when: string): string {
  const iso = dateTimeInputToIso(when)
  return iso ? `В календаре: ${formatDateTime(iso)} · ${zoneTag()}` : zoneFieldHint()
}

interface MeetingFieldsProps {
  value: MeetingDraft
  onChange: (next: MeetingDraft) => void
  /** Текст ошибки поля: сообщение показывается у того поля, о котором оно. */
  error?: string
}

export function MeetingFields({ value, onChange, error }: MeetingFieldsProps) {
  const set = <K extends keyof MeetingDraft>(key: K, next: MeetingDraft[K]) =>
    onChange({ ...value, [key]: next })

  return (
    <div className="flex flex-col gap-4">
      <Field label="Тема" {...(error === 'Укажите тему' ? { error } : {})}>
        {(props) => (
          <Input
            {...props}
            value={value.title}
            onChange={(e) => set('title', e.target.value)}
            placeholder="Встреча с заказчиком"
          />
        )}
      </Field>
      <Field
        label="Когда"
        hint={whenHint(value.when)}
        {...(error === 'Укажите дату и время' ? { error } : {})}
      >
        {(props) => (
          <Input
            {...props}
            type="datetime-local"
            value={value.when}
            onChange={(e) => set('when', e.target.value)}
          />
        )}
      </Field>
      <Field label="Где">
        {(props) => (
          <Input
            {...props}
            value={value.location}
            onChange={(e) => set('location', e.target.value)}
            placeholder="Адрес, переговорная или сервис"
          />
        )}
      </Field>
      <Field label="Ссылка на встречу" hint="Зум, Телемост или любой другой сервис">
        {(props) => (
          <Input
            {...props}
            type="url"
            value={value.url}
            onChange={(e) => set('url', e.target.value)}
            placeholder="https://"
          />
        )}
      </Field>
      <Field label="Участники">
        {(props) => (
          <Input
            {...props}
            value={value.participants}
            onChange={(e) => set('participants', e.target.value)}
            placeholder="Через запятую"
          />
        )}
      </Field>
      <Field label="Заметки">
        {(props) => (
          <Textarea {...props} value={value.notes} onChange={(e) => set('notes', e.target.value)} />
        )}
      </Field>
      <Checkbox
        label={<span className="text-sm text-text-secondary">Видно заказчику</span>}
        checked={value.customer_visible}
        onChange={(e) => set('customer_visible', e.target.checked)}
      />
      <div className="flex flex-col gap-2">
        <span className="label">Вложения</span>
        {value.attachments.map((f) => (
          <FileRow
            key={f.key}
            file={f}
            onRemove={() =>
              set(
                'attachments',
                value.attachments.filter((a) => a.key !== f.key),
              )
            }
          />
        ))}
        <FileDrop
          onUpload={uploadFile}
          onUploaded={(f) => set('attachments', [...value.attachments, f])}
          multiple
        />
      </div>
    </div>
  )
}
